import React from 'react';
import {
  UtensilsCrossed, ChevronLeft, ChevronRight, Plus, Edit, Trash2, CalendarOff, Soup
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import LoadingSpinner from '../LoadingSpinner';
import { formatDateForAPI, formatDateCourte, getJoursDeLaSemaine } from './cantineUtils';

const NOMS_JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const CantineMenusSection = ({
  isLoading,
  lundi,
  menusParDate,
  joursService,
  onPreviousWeek,
  onNextWeek,
  onCurrentWeek,
  onEditMenu,
  onDeleteMenu
}) => {
  const jours = getJoursDeLaSemaine(lundi);
  const aujourdhui = formatDateForAPI(new Date());

  const libelleSemaine = `${formatDateCourte(jours[0])} — ${formatDateCourte(jours[6])}`;
  const nbMenusSaisis = jours.filter((jour) => menusParDate[formatDateForAPI(jour)]).length;

  return (
    <div className="space-y-4">
      {/* Navigation de semaine */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onPreviousWeek}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                title="Semaine précédente"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="px-4 py-2 bg-[#246990] text-white rounded-lg text-sm font-semibold min-w-[200px] text-center">
                Semaine du {libelleSemaine}
              </div>

              <button
                type="button"
                onClick={onNextWeek}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                title="Semaine suivante"
              >
                <ChevronRight size={18} />
              </button>

              <Button size="sm" variant="outline" onClick={onCurrentWeek} className="ml-1">
                Cette semaine
              </Button>
            </div>

            <div className="rounded-lg bg-[#319190]/10 px-4 py-2">
              <p className="text-xs font-semibold uppercase text-[#1f5b5a]">Menus saisis</p>
              <p className="text-sm font-bold text-gray-800 mt-0.5">
                {nbMenusSaisis} / {joursService.length} jour(s) de service
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grille hebdomadaire */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="text-xl text-[#319190] flex items-center gap-2">
              <UtensilsCrossed size={24} />
              Menus de la semaine
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              le menu du jour s'affiche automatiquement sur la feuille de pointage
            </p>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <LoadingSpinner message="Chargement des menus..." color="#319190" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {jours.map((jour, index) => {
                const dateApi = formatDateForAPI(jour);
                const menu = menusParDate[dateApi];
                const numeroJour = index + 1;
                const estJourDeService = joursService.includes(numeroJour);
                const estAujourdhui = dateApi === aujourdhui;

                // Un jour hors service reste affiche mais grise : l'ecole peut
                // exceptionnellement servir un repas ce jour-la.
                const tone = !estJourDeService
                  ? { border: 'border-gray-200', header: 'bg-gray-50 border-gray-200', title: 'text-gray-500' }
                  : menu
                    ? { border: 'border-[#319190]/30', header: 'bg-[#319190]/10 border-[#319190]/30', title: 'text-[#1f5b5a]' }
                    : { border: 'border-orange-200', header: 'bg-orange-50 border-orange-200', title: 'text-orange-900' };

                return (
                  <div
                    key={dateApi}
                    className={`border-2 ${tone.border} rounded-lg overflow-hidden ${estAujourdhui ? 'ring-2 ring-[#246990] ring-offset-1' : ''}`}
                  >
                    <div className={`p-3 ${tone.header} border-b flex items-center justify-between gap-2`}>
                      <div className="min-w-0">
                        <h3 className={`text-base font-semibold ${tone.title} flex items-center gap-2`}>
                          {NOMS_JOURS[index]}
                          {estAujourdhui && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#246990] text-white">
                              Aujourd'hui
                            </span>
                          )}
                        </h3>
                        <p className="text-xs text-gray-600 mt-0.5">{formatDateCourte(jour)}</p>
                      </div>

                      {!estJourDeService && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                          <CalendarOff size={13} />
                          Hors service
                        </span>
                      )}
                    </div>

                    <div className="p-4 bg-white min-h-[132px] flex flex-col justify-between">
                      {menu ? (
                        <>
                          <div className="space-y-2 text-sm">
                            {menu.entree && (
                              <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">Entrée</p>
                                <p className="text-gray-900">{menu.entree}</p>
                              </div>
                            )}
                            {menu.plat && (
                              <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">Plat</p>
                                <p className="text-gray-900 font-medium">{menu.plat}</p>
                              </div>
                            )}
                            {menu.dessert && (
                              <div>
                                <p className="text-xs font-semibold uppercase text-gray-500">Dessert</p>
                                <p className="text-gray-900">{menu.dessert}</p>
                              </div>
                            )}
                            {menu.observations && (
                              <p className="text-xs text-gray-500 italic pt-1">{menu.observations}</p>
                            )}
                          </div>

                          <div className="flex items-center justify-end gap-1.5 mt-3 pt-3 border-t border-gray-100">
                            <button
                              className="p-2 text-[#246990] hover:bg-[#246990]/10 rounded-lg transition-colors"
                              title="Modifier le menu"
                              onClick={() => onEditMenu(jour, menu)}
                            >
                              <Edit size={15} />
                            </button>
                            <button
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Supprimer le menu"
                              onClick={() => onDeleteMenu(jour, menu)}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center py-4">
                          <Soup size={28} className="text-gray-300 mb-2" />
                          <p className="text-sm text-gray-500 mb-3">Aucun menu saisi</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onEditMenu(jour, null)}
                          >
                            <Plus size={14} className="mr-1.5" />
                            Ajouter
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CantineMenusSection;
